import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Key, Webhook, FileText, Trash2, Eye, EyeOff } from "lucide-react";

export default function Integration() {
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [deliveryLog, setDeliveryLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("keys");
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [showCreateWebhook, setShowCreateWebhook] = useState(false);
  const [keyForm, setKeyForm] = useState({ name: "", scopes: "outbound_read" });
  const [webhookForm, setWebhookForm] = useState({ url: "", events: "", secret: "" });
  const [saving, setSaving] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState({});

  const load = () => {
    Promise.all([
      api.get("/api-keys").then((r) => setApiKeys(r.data)).catch(() => {}),
      api.get("/webhook-subscriptions").then((r) => setWebhooks(r.data)).catch(() => {}),
      api.get("/webhook-subscriptions/delivery-log").then((r) => setDeliveryLog(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const createKey = async () => {
    setSaving(true);
    try {
      const r = await api.post("/api-keys", {
        name: keyForm.name,
        scope: keyForm.scopes,
      });
      toast.success("API key created");
      if (r.data.key) {
        await navigator.clipboard.writeText(r.data.key);
        toast.success("Key copied to clipboard");
      }
      setShowCreateKey(false);
      setKeyForm({ name: "", scopes: "outbound_read" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const revokeKey = async (id) => {
    if (!window.confirm("Revoke this API key? This cannot be undone.")) return;
    try {
      await api.delete(`/api-keys/${id}`);
      toast.success("API key revoked");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createWebhook = async () => {
    setSaving(true);
    try {
      await api.post("/webhook-subscriptions", {
        url: webhookForm.url,
        event_types: webhookForm.events.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Webhook created");
      setShowCreateWebhook(false);
      setWebhookForm({ url: "", events: "", secret: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteWebhook = async (id) => {
    if (!window.confirm("Delete this webhook subscription?")) return;
    try {
      await api.delete(`/webhook-subscriptions/${id}`);
      toast.success("Webhook deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const maskKey = (key) => key ? `${key.substring(0, 8)}${"*".repeat(24)}` : "***";

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Settings</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Integrations</h1>
          <p className="text-zinc-500 mt-2 text-sm">API keys, webhook subscriptions and delivery logs.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-zinc-200">
        {[
          { id: "keys", label: "API Keys", icon: Key },
          { id: "webhooks", label: "Webhooks", icon: Webhook },
          { id: "log", label: "Delivery Log", icon: FileText },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : tab === "keys" ? (
        <div className="mt-6">
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowCreateKey(true)}
              className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
              <Plus size={14} /> Create API key
            </button>
          </div>
          {apiKeys.length === 0 ? (
            <div className="border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
              <Key size={26} className="mx-auto mb-3" />
              <p className="text-sm">No API keys yet.</p>
            </div>
          ) : (
            <div className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
              {apiKeys.map((k) => (
                <div key={k.id} className="px-6 py-4 border-b border-zinc-50 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{k.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-xs text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded font-mono">
                        {revealedKeys[k.id] ? k.key_preview || maskKey(k.key) : maskKey(k.key)}
                      </code>
                      <button onClick={() => setRevealedKeys({ ...revealedKeys, [k.id]: !revealedKeys[k.id] })} className="text-zinc-400 hover:text-zinc-600">
                        {revealedKeys[k.id] ? <EyeOff size={12} /> : <Eye size={12} />}
                      </button>
                    </div>
                    <p className="text-[10px] text-zinc-400 mt-1">Scopes: {(k.scopes || []).join(", ") || "all"} | Created: {k.created_at}</p>
                  </div>
                  <button onClick={() => revokeKey(k.id)} className="p-2 rounded-md text-zinc-400 hover:bg-red-50 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : tab === "webhooks" ? (
        <div className="mt-6">
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowCreateWebhook(true)}
              className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
              <Plus size={14} /> Add webhook
            </button>
          </div>
          {webhooks.length === 0 ? (
            <div className="border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
              <Webhook size={26} className="mx-auto mb-3" />
              <p className="text-sm">No webhook subscriptions yet.</p>
            </div>
          ) : (
            <div className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
              {webhooks.map((w) => (
                <div key={w.id} className="px-6 py-4 border-b border-zinc-50 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium font-mono">{w.url}</p>
                    <p className="text-[10px] text-zinc-400 mt-1">Events: {(w.events || []).join(", ")}</p>
                  </div>
                  <button onClick={() => deleteWebhook(w.id)} className="p-2 rounded-md text-zinc-400 hover:bg-red-50 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-6">
          {deliveryLog.length === 0 ? (
            <div className="border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
              <FileText size={26} className="mx-auto mb-3" />
              <p className="text-sm">No delivery log entries.</p>
            </div>
          ) : (
            <div className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                <span className="col-span-3">Event</span>
                <span className="col-span-4">URL</span>
                <span className="col-span-2">Status</span>
                <span className="col-span-3">Timestamp</span>
              </div>
              {deliveryLog.map((l) => (
                <div key={l.id} className="grid grid-cols-12 items-center px-6 py-3 border-b border-zinc-50">
                  <span className="col-span-3 text-sm">{l.event}</span>
                  <span className="col-span-4 text-sm text-zinc-600 font-mono truncate">{l.url}</span>
                  <span className="col-span-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${l.status_code >= 200 && l.status_code < 300 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>
                      {l.status_code}
                    </span>
                  </span>
                  <span className="col-span-3 text-xs text-zinc-400">{l.timestamp}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showCreateKey && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreateKey(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Create API key</h2>
              <button onClick={() => setShowCreateKey(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={keyForm.name} onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })} placeholder="Key name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <select value={keyForm.scopes} onChange={(e) => setKeyForm({ ...keyForm, scopes: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 bg-white">
                <option value="outbound_read">outbound_read</option>
                <option value="webhook_management">webhook_management</option>
                <option value="inbound_receiver">inbound_receiver</option>
              </select>
              <button onClick={createKey} disabled={!keyForm.name || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create key"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateWebhook && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreateWebhook(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Add webhook</h2>
              <button onClick={() => setShowCreateWebhook(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={webhookForm.url} onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })} placeholder="Webhook URL (https://...)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={webhookForm.events} onChange={(e) => setWebhookForm({ ...webhookForm, events: e.target.value })} placeholder="Events (comma separated, e.g. order.created, sample.approved)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={webhookForm.secret} onChange={(e) => setWebhookForm({ ...webhookForm, secret: e.target.value })} placeholder="Signing secret (optional)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <button onClick={createWebhook} disabled={!webhookForm.url || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Adding..." : "Add webhook"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
